import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, Building2, Shield, FileCheck, AlertTriangle, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';

/**
 * BUSINESS MEMBERSHIP EXPLANATION PAGE — LOCKED DIRECTIVE
 * 
 * Business Membership = Access + Eligibility
 * Business membership does NOT automatically grant sponsorship placement.
 * 
 * This page must clearly explain:
 * - What Business membership provides
 * - What Business membership does NOT include
 * - The relationship to sponsorships
 * - How sponsorships work on LikeKind
 */

export default function MembershipBusiness() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="flex items-center p-4">
        <button
          onClick={() => navigate('/membership')}
          className="p-2 rounded-lg"
          style={{ color: 'var(--fy-text-secondary)' }}
          data-testid="back-to-membership"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </header>

      {/* Content */}
      <main className="px-6 pb-12">
        <div className="max-w-md mx-auto">
          {/* Icon and Title */}
          <div className="text-center mb-6">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: '#6B728015' }}
            >
              <Building2 className="w-8 h-8" style={{ color: '#6B7280' }} />
            </div>
            <h1 
              className="text-2xl font-bold mb-2"
              style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}
              data-testid="business-membership-title"
            >
              Business Membership
            </h1>
            <p 
              className="text-lg font-medium"
              style={{ color: '#6B7280' }}
            >
              Access + Eligibility
            </p>
          </div>

          {/* Description */}
          <p 
            className="text-center mb-8"
            style={{ color: 'var(--fy-text-secondary)' }}
          >
            Represent your business on LikeKind with integrity. Business membership provides eligibility to apply for sponsorship placements — not advertising.
          </p>

          {/* WHAT IT PROVIDES */}
          <div className="mb-6">
            <h2 
              className="font-bold text-sm uppercase tracking-wide mb-3"
              style={{ color: 'var(--fy-text-disabled)' }}
            >
              What Business membership provides
            </h2>
            <ul className="space-y-2">
              {[
                'Eligibility to apply for sponsorship placements',
                'Ability to represent a real business identity',
                'Access to Commons stewardship (where applicable)',
                'Ability to host long-term groups (non-social, non-algorithmic)',
                'Professional presence without engagement games',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A8F5B' }} />
                  <span className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* WHAT IT DOES NOT INCLUDE */}
          <div className="mb-6">
            <h2 
              className="font-bold text-sm uppercase tracking-wide mb-3"
              style={{ color: 'var(--fy-text-disabled)' }}
            >
              What Business membership does NOT include
            </h2>
            <ul className="space-y-2">
              {[
                'Guaranteed visibility or placement',
                'Advertising tools or ad creation',
                'Analytics, tracking, or targeting',
                'Engagement metrics or impression counts',
                'Automatic sponsorship approval',
                'Any form of behavioral data',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                  <span className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ABOUT SPONSORSHIPS SECTION */}
          <div 
            className="rounded-2xl p-5 mb-6"
            style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
          >
            <div className="flex items-start gap-3 mb-4">
              <Shield className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#7A8F5B' }} />
              <h2 
                className="font-bold"
                style={{ color: 'var(--fy-text-primary)' }}
              >
                About Sponsorships on LikeKind
              </h2>
            </div>
            
            <p 
              className="text-sm mb-4"
              style={{ color: 'var(--fy-text-secondary)' }}
            >
              Sponsorships are separate from membership. They are manual, vetted, and governed by strict rules:
            </p>

            <ul className="space-y-2 mb-4">
              {[
                'All placements are static and pre-paid',
                'Every placement is reviewed by humans',
                'No tracking pixels, embeds, or scripts',
                'No behavioral targeting of any kind',
                'No child-directed content permitted',
                'No video content in current version',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <FileCheck className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A8F5B' }} />
                  <span className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>{item}</span>
                </li>
              ))}
            </ul>

            <div 
              className="p-3 rounded-xl text-xs"
              style={{ backgroundColor: 'var(--fy-background-primary)' }}
            >
              <p className="font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                Important distinction:
              </p>
              <p style={{ color: 'var(--fy-text-secondary)' }}>
                <strong>Sponsorship ≠ advertising.</strong> Placement ≠ promotion. Visibility ≠ influence. Sponsors fund the platform. They do not shape it.
              </p>
            </div>
          </div>

          {/* CHILD SAFETY WARNING */}
          <div 
            className="rounded-2xl p-4 mb-6"
            style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#dc2626' }} />
              <div>
                <p 
                  className="text-sm font-medium mb-1"
                  style={{ color: '#991b1b' }}
                >
                  Child Safety Enforcement
                </p>
                <p 
                  className="text-xs"
                  style={{ color: '#991b1b' }}
                >
                  Any sponsorship that risks exposure to minors, links to mixed-audience content, or changes after approval results in immediate permanent ban. No exceptions. No appeals.
                </p>
              </div>
            </div>
          </div>

          {/* POLICY LINKS */}
          <div className="space-y-2 mb-6">
            <a
              href="/sponsor-policy"
              className="flex items-center justify-between p-3 rounded-xl transition-colors hover:bg-opacity-80"
              style={{ backgroundColor: 'var(--fy-surface-muted)' }}
              data-testid="sponsor-policy-link"
            >
              <span className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                Read Sponsor Policy
              </span>
              <ExternalLink className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
            </a>
            <a
              href="/about-terms"
              className="flex items-center justify-between p-3 rounded-xl transition-colors hover:bg-opacity-80"
              style={{ backgroundColor: 'var(--fy-surface-muted)' }}
            >
              <span className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                Trust & Safety
              </span>
              <ExternalLink className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
            </a>
          </div>

          {/* CTA */}
          <Button
            onClick={() => navigate('/membership/business/payment')}
            className="w-full h-12 rounded-full font-bold"
            style={{ backgroundColor: '#6B7280', color: '#FFFFFF' }}
            data-testid="continue-to-payment"
          >
            Continue
          </Button>

          {/* Back link */}
          <button
            onClick={() => navigate('/membership')}
            className="w-full text-center text-sm mt-4 py-2"
            style={{ color: 'var(--fy-text-secondary)' }}
          >
            Choose a different option
          </button>
        </div>
      </main>
    </div>
  );
}
